import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X, Plus, Minus } from 'lucide-react';
import { PListLineDto } from '../../../../../types/picking';
import { isOverPickAllowed } from '../hooks/usePickingData';
import { NumericInput } from '../../../../common/inputs';

interface ExtendedPickingLine extends PListLineDto {
  isPartiallyPicked?: boolean;
  isFullyPicked?: boolean;
  image?: string;
  requireLocationScan?: boolean;
  requireItemScan?: boolean;
  palletId?: string;
  itemDescription?: string;
  location?: string;
}

interface QuantityConfirmModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  proposedQuantity: number;
  /**
   * 'add' (default): skann-flyt — bekreftet antall LEGGES TIL qtyPicked (validering 1..gjenstående).
   * 'setTotal': redigering — bekreftet antall er NY TOTAL plukket (validering 0..bestilt, 0 = null linjen).
   */
  mode?: 'add' | 'setTotal';
  onConfirm: (quantity: number) => void;
  /** «Bekreft tomt» (Ariens): meld linjen som manko i stedet for å plukke. Skjules hvis ikke satt. */
  onReportMissing?: () => void;
  onClose: () => void;
}

export const QuantityConfirmModal: React.FC<QuantityConfirmModalProps> = ({
  isOpen,
  line,
  proposedQuantity,
  mode = 'add',
  onConfirm,
  onReportMissing,
  onClose
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(proposedQuantity);
  // Så lenge foreslått antall er urørt, markeres hele feltet ved fokus/trykk — første
  // tastetrykk erstatter da forslaget i stedet for å legges bak (nettbrett-vennlig).
  const pristineRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setQuantity(proposedQuantity);
      pristineRef.current = true;
    }
  }, [isOpen, proposedQuantity]);

  // Markeringen gjøres i neste frame: Android setter ellers markøren etter fokus og
  // opphever markeringen igjen.
  const selectAll = (input: HTMLInputElement) => {
    requestAnimationFrame(() => input.select());
  };

  // autoFocus ved montering utløser ikke onFocus — marker eksplisitt når modalen åpnes.
  // Avhenger også av quantity: ved gjenåpning står forrige antall i state i første render,
  // og når forslaget skrives inn etterpå nullstiller verdibyttet markeringen. Kjøres derfor
  // etter hver verdiendring så lenge feltet er urørt.
  useLayoutEffect(() => {
    if (!isOpen || !pristineRef.current) return;
    const input = inputRef.current;
    if (!input) return;
    if (document.activeElement !== input) input.focus();
    input.select();
    selectAll(input);
  }, [isOpen, line, quantity]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => selectAll(e.currentTarget);

  const handlePointerUp = (e: React.PointerEvent<HTMLInputElement>) => {
    if (pristineRef.current) selectAll(e.currentTarget);
  };

  if (!isOpen || !line) return null;

  const isSetTotal = mode === 'setTotal';
  const remaining = line.qty - line.qtyPicked;
  const minAllowed = isSetTotal ? 0 : 1;
  // Overplukk tillatt (TW 36401594, bizClick-regel ExtraQuantityPickedYesNo / manuell linje): ingen øvre grense
  const overPickAllowed = isOverPickAllowed(line);
  const maxAllowed = overPickAllowed ? Number.POSITIVE_INFINITY : (isSetTotal ? line.qty : remaining);
  const isValid = quantity >= minAllowed && quantity <= maxAllowed;

  const handleIncrement = () => {
    pristineRef.current = false;
    if (quantity < maxAllowed) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrement = () => {
    pristineRef.current = false;
    if (quantity > minAllowed) {
      setQuantity(quantity - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pristineRef.current = false;
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setQuantity(value);
    } else if (e.target.value === '') {
      setQuantity(0);
    }
  };

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(quantity);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    // Øverst, ikke sentrert: på nettbrett legger tastaturet seg over siden uten å krympe den,
    // og en sentrert dialog fikk antallsfeltet bak tastaturet
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-full sm:rounded-lg p-4 sm:p-6 sm:max-w-md overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {isSetTotal
              ? t('warehouse.modules.picking.quantityConfirm.editTitle', {}, 'Endre antall')
              : t('warehouse.modules.picking.confirmPick')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">{t('common.fields.itemNumber')}</div>
          <div className="font-medium mb-2">{line.itemNo}</div>

          <div className="text-sm text-gray-600 mb-1">{t('warehouse.modules.picking.labels.description')}</div>
          <div className="font-medium mb-3">{line.itemDescription || t('common.fields.description')}</div>

          {(line.location || line.binDescription1 || line.binCode) && (
            <>
              <div className="text-sm text-gray-600 mb-1">{t('warehouse.modules.picking.labels.location')}</div>
              <div className="font-medium mb-3">{line.location || line.binDescription1 || line.binCode}</div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.picked')}</div>
              <div className="font-semibold text-blue-600">{line.qtyPicked}</div>
            </div>
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.labels.ordered')}</div>
              <div className="font-semibold">{line.qty}</div>
            </div>
            <div>
              <div className="text-gray-600">{t('warehouse.modules.picking.labels.remaining')}</div>
              <div className="font-semibold text-orange-600">{remaining}</div>
            </div>
          </div>
        </div>

        {/* Quantity input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isSetTotal
              ? t('warehouse.modules.picking.quantityConfirm.newTotalLabel', {}, 'Nytt plukket antall')
              : t('warehouse.modules.picking.pickQuantity')}
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDecrement}
              disabled={quantity <= minAllowed}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded-lg transition-colors"
            >
              <Minus size={20} />
            </button>

            <NumericInput
              ref={inputRef}
              mode="decimal"
              quantity
              min={minAllowed}
              max={Number.isFinite(maxAllowed) ? maxAllowed : undefined}
              value={quantity}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onFocus={handleFocus}
              onPointerUp={handlePointerUp}
              className="flex-1 text-center text-2xl font-semibold p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />

            <button
              onClick={handleIncrement}
              disabled={quantity >= maxAllowed}
              className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded-lg transition-colors"
            >
              <Plus size={20} />
            </button>
          </div>

          {!isValid && (
            <p className="mt-2 text-sm text-red-600">
              {isSetTotal
                ? t('warehouse.modules.picking.quantityConfirm.cannotExceedOrdered', { 0: line.qty }, `Antall kan ikke overstige bestilt (${line.qty})`)
                : quantity <= 0
                  ? t('warehouse.modules.picking.quantityConfirm.mustBeGreaterThanZero')
                  : t('warehouse.modules.picking.quantityConfirm.cannotExceedRemaining', { 0: remaining })}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium min-h-[44px] order-1"
          >
            {t('common.buttons.confirm')}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2"
          >
            {t('common.buttons.cancel')}
          </button>
        </div>

        {/* «Bekreft tomt» (Ariens): kun i skann-flyt og når plukk ikke er påbegynt (backend avviser ellers) */}
        {!isSetTotal && onReportMissing && line.qtyPicked === 0 && (
          <button
            onClick={onReportMissing}
            className="w-full mt-3 py-2.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors text-sm font-medium min-h-[44px]"
          >
            {t('warehouse.modules.picking.quantityConfirm.reportMissing', {}, 'Finner ikke / tomt – meld manko')}
          </button>
        )}
      </div>
    </div>
  );
};
